import { getEmptyParagraphNode } from '@doltech/utils/lib/editor';
import { trimEnd } from 'lodash';
import { nanoid } from 'nanoid';
import { Editor, Node, Transforms } from 'slate';

import { ELEMENT_PARAGRAPH } from '../../../../../slatePlugins';
import { findNodeByElementId, getNodeText } from '../../shared-transforms';
import { ELEMENT_QUOTE, ELEMENT_QUOTE_AUTHOR, ELEMENT_QUOTE_CONTENT } from '../QuotePlugin.types';

function generateSingleQuoteContentNode(text = '') {
  return {
    id: nanoid(),
    type: ELEMENT_QUOTE_CONTENT,
    children: [{ text }],
  } as Node;
}

function generateSingleQuoteAuthorNode(text = '') {
  return {
    id: nanoid(),
    type: ELEMENT_QUOTE_AUTHOR,
    children: [{ text }],
  } as Node;
}

function generateSingleQuoteNode(text = '') {
  return {
    id: nanoid(),
    type: ELEMENT_QUOTE,
    children: [generateSingleQuoteContentNode(text), generateSingleQuoteAuthorNode('')],
  } as Node;
}

function paragraphToQuote({ editor }) {
  const nodeEntries = Editor.nodes(editor, { at: editor.selection }) as any;
  let text = '';
  const collectedPaths = [];

  for (const [node, path] of nodeEntries) {
    if (node?.type === ELEMENT_PARAGRAPH) {
      const nodeText = getNodeText(node);
      text += nodeText;
      collectedPaths.push(path);
      if (nodeText.trim().length > 0) {
        text += '\n';
      }
    }
  }

  text = trimEnd(text, '\n');
  collectedPaths.reverse();
  collectedPaths.forEach((path) => {
    Transforms.setPoint(editor, path, { edge: 'start' });
    Transforms.delete(editor, { at: path });
  });
  const insertPath = collectedPaths[0];
  Transforms.insertNodes(editor, generateSingleQuoteNode(text), {
    at: insertPath,
  });
  Transforms.select(editor, insertPath);
  Transforms.collapse(editor, { edge: text.length > 0 ? 'end' : 'start' });
}

function quoteBlockToParagraph({ editor, quoteNode }) {
  const [node, path] = quoteNode;
  const text = getNodeText(node.children[0]);
  const splitBreakLine = text.split(/\n/);
  Transforms.delete(editor, { at: path });
  Transforms.insertNodes(
    editor,
    splitBreakLine.map((txt) => getEmptyParagraphNode(txt)),
    { at: path }
  );
  Transforms.select(editor, path);
  Transforms.collapse(editor, { edge: 'end' });
}

function addOrDeleteQuote(editor) {
  if (!editor) {
    // eslint-disable-next-line no-console
    console.error('[CardLogic] Unable to find editor, did you forgot to register editor');
    return;
  }
  const isSelection = Boolean(editor.selection);

  // Prevent add quote inside quote
  if (isSelection) {
    const [foundNode] = Editor.nodes(editor, {
      at: editor.selection,
      match: (node: any) => node?.type === ELEMENT_QUOTE,
    });

    if (foundNode) {
      quoteBlockToParagraph({ editor, quoteNode: foundNode });
      return;
    }
    paragraphToQuote({ editor });
  }
}

function focusOnContent({ editor, elementId }) {
  const foundNode = findNodeByElementId({ editor, elementId });
  if (foundNode) {
    const [, findPath] = foundNode;
    const nodeEntries = Editor.nodes(editor, { at: findPath }) as any;

    let contentNode = null;
    for (const [node, path] of nodeEntries) {
      if (node?.type === ELEMENT_QUOTE_CONTENT) {
        contentNode = [node, path];
      }
    }

    if (contentNode) {
      const [, path] = contentNode;
      const newPath = Editor.end(editor, path);
      Transforms.select(editor, newPath);
    }
  }
}

function setCiteLink({ editor, elementId, value }) {
  Transforms.setNodes(
    editor,
    {
      citeLink: value,
    },
    {
      at: [],
      match: (node: any) => {
        return node.id === elementId;
      },
    }
  );
}

function setCiteTitle({ editor, elementId, value }) {
  Transforms.setNodes(
    editor,
    {
      title: value,
    },
    {
      at: [],
      match: (node: any) => {
        return node.id === elementId;
      },
    }
  );
}

export const QuoteElementTransformActions = {
  generateSingleQuoteContentNode,
  generateSingleQuoteAuthorNode,
  addOrDeleteQuote,
  focusOnContent,
  setCiteLink,
  setCiteTitle,
};
